import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * How often to poll the server for a newer service worker while a tab stays
 * open. Chat/dashboard tabs here live for hours, so an hourly `registration
 * .update()` (plus a check whenever the tab is refocused) keeps a long-lived
 * session from missing a deploy.
 */
const CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly

/** Floor between two refocus-triggered checks — see `onRegisteredSW`. */
const REFOCUS_CHECK_MIN_GAP_MS = 60_000;

/** If the waiting worker never takes control within this window, surface the
 *  Failed state instead of spinning forever. */
export const APPLY_TIMEOUT_MS = 10_000;

export type ApplyState = "idle" | "applying" | "failed";

/** Verdict of the "is the page already on the waiting worker's build?" check —
 *  "pending" until it has run (or while nothing is waiting). */
type WaitingBuildCheck = "pending" | "same" | "different";

interface AppUpdateValue {
  /**
   * True once a newly built SW is sitting in `waiting` (prompt mode) AND the page
   * is not already running that worker's build. The second half matters after a
   * plain browser refresh: the document loads the new bundle while the new worker
   * still waits for the old tabs to close, so `needRefresh` alone would prompt a
   * user who has nothing left to update. See the check below.
   */
  updateReady: boolean;
  /** Where the apply attempt stands. Shared, so every mount of the prompt (the
   *  expanded card and the always-mounted collapsed twin) shows the same state. */
  applyState: ApplyState;
  /** Posts SKIP_WAITING to the waiting SW and reloads once it takes control. */
  applyUpdate: () => Promise<void>;
}

const AppUpdateContext = createContext<AppUpdateValue | null>(null);

/** Stable inert value for callers outside the provider — a fresh object each
 *  render would change `applyUpdate`'s identity and bust consumers' memos. */
const INERT_APP_UPDATE: AppUpdateValue = {
  updateReady: false,
  applyState: "idle",
  applyUpdate: async () => {},
};

/**
 * Registers the service worker exactly once for the whole app and shares its
 * update state via context.
 *
 * `useRegisterSW` calls `registerSW()` in a `useState` initializer, so it runs
 * once per hook instance — every extra caller is another `Workbox` object,
 * another `register()`, and its own `needRefresh`. Registering here (mounted a
 * single time near the app root, beside `StreamingProvider`) keeps it to one
 * registration and one source of truth, so the card, the Slack-slot decision,
 * and the click's skip-waiting all read the same worker.
 *
 * The apply state machine lives here too: both prompt mounts read one
 * `applyState`, so collapsing the rail mid-apply keeps the spinner (rather than
 * showing an idle, clickable twin) and a failure shows on whichever is visible.
 */
export function AppUpdateProvider({ children }: { children: ReactNode }) {
  const [applyState, setApplyState] = useState<ApplyState>("idle");
  const [waitingBuildCheck, setWaitingBuildCheck] = useState<WaitingBuildCheck>("pending");
  // Bumped whenever a NEW worker reaches `installed`, so a genuinely newer deploy
  // re-runs the comparison instead of resting on the previous worker's verdict.
  const [checkTick, setCheckTick] = useState(0);
  const applyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);
  const updateFoundRef = useRef<{ registration: ServiceWorkerRegistration; handler: () => void } | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      let lastCheckAt = 0;
      const check = () => {
        lastCheckAt = Date.now();
        // `update()` rejects when the tab is offline or the server is
        // unreachable — expected on a flaky network, and the next tick retries,
        // so swallow it rather than let Sentry's unhandled-rejection handler
        // ship one event per laptop wake.
        registration.update().catch(() => {});
      };
      intervalRef.current = setInterval(check, CHECK_INTERVAL_MS);
      // A backgrounded tab's hourly timer can be throttled, so a deploy that
      // lands while the tab is hidden might go unnoticed until the user returns.
      // Re-check on refocus to close that gap — but throttled, since alt-tabbing
      // is frequent and one check a minute is enough to catch a deploy.
      const onVisibility = () => {
        if (document.hidden) return;
        if (Date.now() - lastCheckAt < REFOCUS_CHECK_MIN_GAP_MS) return;
        check();
      };
      visibilityHandlerRef.current = onVisibility;
      document.addEventListener("visibilitychange", onVisibility);
      // Re-run the "same build?" comparison whenever a NEW worker reaches
      // `installed` (= waiting). `needRefresh` is a boolean that stays true from
      // the first waiting worker onwards, so without this a suppressed prompt
      // would never notice a genuinely newer deploy landing behind it.
      const onUpdateFound = () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (installing.state === "installed") setCheckTick((t) => t + 1);
        });
      };
      updateFoundRef.current = { registration, handler: onUpdateFound };
      registration.addEventListener("updatefound", onUpdateFound);
    },
    onRegisterError(err) {
      // A failed SW registration is not user-actionable; `debug` keeps it out of
      // Sentry's console capture (which forwards warn/error) while staying
      // visible locally.
      console.debug("[sw] register failed:", err);
    },
  });

  // The provider mounts once near the app root, but tear its timers/listener
  // down anyway so tests (and any future remount) don't leak them.
  useEffect(
    () => () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (applyTimeoutRef.current) clearTimeout(applyTimeoutRef.current);
      if (visibilityHandlerRef.current) {
        document.removeEventListener("visibilitychange", visibilityHandlerRef.current);
      }
      if (updateFoundRef.current) {
        updateFoundRef.current.registration.removeEventListener("updatefound", updateFoundRef.current.handler);
      }
    },
    [],
  );

  // Does the page already run the build the waiting worker carries?
  //
  // After a plain browser refresh the page loads the NEW bundle, but the new
  // service worker is still `waiting` (it only activates once every tab on the
  // old worker is gone), so `needRefresh` fires again and the prompt nags a user
  // who is already up to date. There is no build id to compare, so compare what
  // the page actually loaded against the waiting worker's precache manifest: if
  // every script/stylesheet this document pulled is listed in that worker's
  // source, the worker has nothing new to give us.
  //
  // A worker suppressed this way still activates on its own once all iQ tabs are
  // closed. We deliberately do NOT skip-waiting it silently — the plugin's
  // `controlling` listener would reload the page under the user.
  useEffect(() => {
    if (!needRefresh) {
      setWaitingBuildCheck("pending");
      return;
    }
    let cancelled = false;
    void (async () => {
      const reg = await navigator.serviceWorker?.getRegistration();
      const waiting = reg?.waiting;
      if (!waiting || cancelled) return;
      let swText: string;
      try {
        const res = await fetch(waiting.scriptURL, { cache: "no-store" });
        swText = await res.text();
      } catch {
        // Can't tell — assume it really is a new build rather than swallow a
        // legitimate update.
        if (!cancelled) setWaitingBuildCheck("different");
        return;
      }
      if (cancelled) return;
      // Same-origin only: index.html also links third-party stylesheets (Google
      // Fonts), which are never in the precache manifest and would otherwise make
      // the check fail on every page load.
      const assets = Array.from(
        document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>(
          'script[type="module"][src], link[rel="stylesheet"][href]',
        ),
      )
        .map((el) => new URL(el instanceof HTMLScriptElement ? el.src : el.href, document.baseURI))
        .filter((url) => url.origin === window.location.origin)
        .map((url) => url.pathname.replace(/^\//, ""));
      const same = assets.length > 0 && assets.every((path) => swText.includes(path));
      setWaitingBuildCheck(same ? "same" : "different");
    })();
    return () => {
      cancelled = true;
    };
  }, [needRefresh, checkTick]);

  const applyUpdate = useCallback(async () => {
    setApplyState("applying");
    if (applyTimeoutRef.current) clearTimeout(applyTimeoutRef.current);
    applyTimeoutRef.current = setTimeout(() => setApplyState("failed"), APPLY_TIMEOUT_MS);
    try {
      await updateServiceWorker(true);
      // On success the new worker takes control and the page reloads, so the
      // prompt unmounts before the timeout fires. If it somehow resolves
      // without reloading, the timeout still surfaces the Failed state.
    } catch {
      if (applyTimeoutRef.current) clearTimeout(applyTimeoutRef.current);
      setApplyState("failed");
    }
  }, [updateServiceWorker]);

  const value = useMemo<AppUpdateValue>(
    () => ({
      updateReady: needRefresh && waitingBuildCheck === "different",
      applyState,
      applyUpdate,
    }),
    [needRefresh, waitingBuildCheck, applyState, applyUpdate],
  );

  return <AppUpdateContext.Provider value={value}>{children}</AppUpdateContext.Provider>;
}

/**
 * Reads the shared app-update state. Returns an inert default outside the
 * provider (e.g. in tests that don't mount it), so callers never crash.
 */
export function useAppUpdate(): AppUpdateValue {
  return useContext(AppUpdateContext) ?? INERT_APP_UPDATE;
}
